import { isDrawingFormCategory, TicketingFormCategory, MembershipValidityPeriod } from "@simplyk/common";
import * as uuid from "uuid";

import { useTranslate } from "../../../../../../common/hooks/useTranslate";
import { useStableLiveFormEditorContext } from "../../../common/LiveFormEditorContext";
import { getRatesDefaultInformations } from "../stepTickets.helper";

import { RateData } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/types";
import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";

const DefaultMaximumTicketToByPerRate = 10;

interface BuildDefaultRateProps {
  ratesLength: number;
}

export const useBuildDefaultRate = () => {
  const { t } = useTranslate();
  const { apiFormObject: apiTicketing } = useStableLiveFormEditorContext<"ticketing">();
  const { formCategory } = apiTicketing;
  const isSweepstakes = isSweepstakesCategory(formCategory);

  return ({ ratesLength }: BuildDefaultRateProps): RateData => {
    const defaultInformations = getRatesDefaultInformations({
      formCategory,
      t,
      toOmit: isDrawingFormCategory(formCategory) ? "title2" : undefined,
    });

    const getDefaultRateName = () => {
      if (ratesLength === 0) {
        return defaultInformations.rate1Title ?? "";
      } else if (ratesLength === 1) {
        return defaultInformations.rate2Title ?? "";
      }
      return "";
    };

    const getDefaultDescription = () => {
      if (ratesLength === 0) {
        return defaultInformations.rate1Description ?? "";
      } else if (ratesLength === 1) {
        return defaultInformations.rate2Description ?? "";
      }
      return "";
    };

    const membershipValidityPeriod =
      formCategory === TicketingFormCategory.MembershipV2 ? MembershipValidityPeriod.NoExpiration : null;

    return {
      id: uuid.v4(),
      name: getDefaultRateName(),
      amount: undefined,
      description: getDefaultDescription(),
      isGroup: isSweepstakes,
      attendeesByTicket: isSweepstakes ? 1 : null,
      isGeneratingReceipt: false,
      receiptEligibleAmount: null,
      seats: null,
      rateMinimumNumberToBuy: null,
      alreadyBoughtTicketsCount: undefined,
      rateMaximumNumberToBuy: DefaultMaximumTicketToByPerRate,
      membershipValidityPeriod,
      membershipValidityPeriodAtDate: undefined,
      allowAutomaticRenewal: false,
      isScheduled: false,
      salesStartMode: "formDefault" as const,
      salesStartDate: null,
      salesStartAfterRateId: null,
      salesEndMode: "formDefault" as const,
      salesEndDate: null,
      bidIncrement: 500,
      photos: [{ photoUrl: null, id: uuid.v4() }],
      isPayWhatYouCan: false,
      payWhatYouCanMinimumAmount: null,
      payWhatYouCanSuggestedAmount: null,
      isMemberOnly: false,
      memberOnlyMembershipRateIds: [],
      isHidden: false,
      shopVariants: [],
      shopVariantsToDelete: [],
      categoryIds: [],
      taxIds: [],
    };
  };
};
